import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { NewExclusionListWebComponentSchema } from '../types';
import { WEB_COMPONENT_EXCLUSION_LIST_FORM, WEB_COMPONENT_EXCLUSION_ITEM_FORM, WEB_COMPONENT_EXCLUSION_ITEM_GRID, WEB_COMPONENT_GZ_TITLE } from '../../../webcomponents.constants';
import { Subscription } from 'rxjs';
import { getFileModules, getObjectTypeTooltipValues, getObjectTypeValues, ObjectType } from '../../exclusionsPanel/types';
import { translateFn } from '../../../utils/translator.helper';
import { Rights } from '@nirvana/shared/constants';

export class NewExclusionListView extends BaseWebComponentView {
  private subscription: Subscription | null = null;
  private isList?: boolean;
  private listId?: string;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$exclusionListForm = this.shadow.querySelector(WEB_COMPONENT_EXCLUSION_LIST_FORM);
    this.dom.$exclusionItemForm = this.shadow.querySelector(WEB_COMPONENT_EXCLUSION_ITEM_FORM);
    this.dom.$exclusionItemGrid = this.shadow.querySelector(WEB_COMPONENT_EXCLUSION_ITEM_GRID);
    this.dom.$pageTitle = this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE);
    this.dom.$btnSave = this.shadow.getElementById('btnSave');
    this.dom.$btnCancel = this.shadow.getElementById('btnCancel');
    this.isList = this.shadow.host.isList;
    this.listId = this.shadow.host.listId;
    this.setTranslatedStrings();
    this.injectDependenciesInChildren();
  }

  setTranslatedStrings() {
    this.dom.$btnSave.innerHTML = this.commonStrings.save;
    this.dom.$btnCancel.innerHTML = this.commonStrings.cancel;
  }

  async buildView(data: NewExclusionListWebComponentSchema) {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    this.isList = this.shadow.host.isList;
    this.listId = this.shadow.host.listId;

    this.buildNewExclusionItemForm();

    if (this.isList) {
      if (this.listId) {
        this.dom.$pageTitle.innerText = translateFn('Edit Exclusion List');
      } else {
        this.dom.$pageTitle.innerText = translateFn('Add Exclusion List');
      }
      await this.buildNewExclusionListForm(data.response);
    } else {
      this.dom.$pageTitle.innerText = translateFn('Add Exclusions');
      this.dom.$exclusionListForm.style.display = 'none';
      this.dom.$btnSave.innerText = this.commonStrings.save;
      this.dom.$btnSave.disabled = false;
    }
  }

  private async buildNewExclusionListForm(listDetails) {
    this.dom.$exclusionListForm.style.display = 'block';
    this.dom.$btnSave.disabled = true;

    if (this.isList && !this.listId) {
      this.dom.$btnSave.innerText = this.commonStrings.save;
    }

    let descriptionInput = '';
    let titleInput = '';

    if (this.isList && this.listId) {
      this.dom.$btnSave.innerText = this.commonStrings.update;
      this.refreshGrid(this.listId);
      let listData: any = { data: { details: { _id: '', name: '', description: '' } } };
      if (listDetails) {
        listData = listDetails;
      }

      descriptionInput = listData?.data?.details?.description;
      titleInput = listData?.data?.details?.name;
    }

    this.dom.$exclusionListForm.infoString = translateFn('Enter a name and a description to save the list.');
    this.dom.$exclusionListForm.titleInputLabel = translateFn('Name');
    this.dom.$exclusionListForm.titleInputPlaceholder = translateFn('Name');
    this.dom.$exclusionListForm.descriptionInputLabel = translateFn('Description');
    this.dom.$exclusionListForm.descriptionValidationMsg = translateFn('The description must have a maximum of {nameMaxLength} characters.', {nameMaxLength: 128});
    this.dom.$exclusionListForm.nameValidationMsg = translateFn('The name must have a maximum of {nameMaxLength} characters.', {nameMaxLength: 64});
    this.dom.$exclusionListForm.descriptionInputPlaceholder = translateFn('type here');
    this.dom.$exclusionListForm.clearBtnString = translateFn('Clear');
    this.dom.$exclusionListForm.importBtnString = translateFn('Import');
    this.dom.$exclusionListForm.hasManageRights = Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);
    this.dom.$exclusionListForm.titleInput.querySelector('gz-input').setFormValue(titleInput);
    this.dom.$exclusionListForm.titleInput.querySelector('gz-input').markAsValid();
    this.dom.$exclusionListForm.descriptionInput.querySelector('gz-input').setFormValue(descriptionInput);

    this.subscription = this.dom.$exclusionListForm.formGroup.valid$.subscribe({
      next: valid => {
        this.dom.$btnSave.disabled = !valid;
      },
    });
  }

  private buildNewExclusionItemForm() {
    this.dom.$exclusionItemForm.setAttribute('objectTypeLabel', translateFn('Object type'));
    this.dom.$exclusionItemForm.setAttribute('objectTypePlaceholder', translateFn('Select'));
    this.dom.$exclusionItemForm.setAttribute('excludedItemLabel', translateFn('Excluded item'));
    this.dom.$exclusionItemForm.setAttribute('excludedItemPlaceholder', translateFn('type here'));
    this.dom.$exclusionItemForm.setAttribute('modulesLabel', translateFn('Modules'));
    this.dom.$exclusionItemForm.setAttribute('modulesLabelWidth', '240');
    this.dom.$exclusionItemForm.setAttribute('modulesSearchPlaceholderString', translateFn('Select'));
    this.dom.$exclusionItemForm.setAttribute('modulesConfirmActionButtonString', translateFn('Apply'));
    this.dom.$exclusionItemForm.setAttribute('modulesNoSearchResultsString', translateFn('No results found'));
    this.dom.$exclusionItemForm.setAttribute('modulesListTitle', translateFn('All'));
    this.dom.$exclusionItemForm.setAttribute('modulesAllItemsLabel', translateFn('All'));
    this.dom.$exclusionItemForm.setAttribute('modulesCanSelectAll', true);
    this.dom.$exclusionItemForm.setAttribute('remarksLabel', translateFn('Remarks'));
    this.dom.$exclusionItemForm.setAttribute('remarksPlaceholder', translateFn('type here'));
    this.dom.$exclusionItemForm.setAttribute('objectTypeValue', ObjectType.FILE);
    const objectTypeOptions = getObjectTypeValues().map(item => ({
      title: item.label,
      value: item.value,
    }));
    this.dom.$exclusionItemForm.setAttribute('objectTypeOptions', JSON.stringify(objectTypeOptions));
    this.dom.$exclusionItemForm.setAttribute('modulesOptions', JSON.stringify(getFileModules()));
    const tooltipTexts = getObjectTypeTooltipValues().map(item => ({
      text: item.text,
      value: item.value,
    }));
    this.dom.$exclusionItemForm.setAttribute('tooltipTexts', JSON.stringify(tooltipTexts));
  }

  protected onDisconnect() {
    this.subscription?.unsubscribe();
  }

  private injectDependenciesInChildren() {
    if (this.dom.$exclusionItemGrid.injectDependencies) {
      this.dom.$exclusionItemGrid.injectDependencies(this.dependencies);
      this.dom.$exclusionItemGrid.start().catch(console.error);
    }
  }

  refreshGrid(listId: any) {
    const exclusionItemGrid = this.dom.$exclusionItemGrid;
    const gridView = exclusionItemGrid.controller.view.dom.$gridView;
    gridView.extraParams = {
      companyId: Application.Main.getActiveCompany().id,
      listId: listId,
    };
    gridView.controller.handleGridItemsLoad();
  }
}
